import { IconMap } from '@shared/common/icons';
import { ClinicianDashboardRoutes } from '@shared/common/routing/clinician-dashboard-routes';
import { IRouteStructure } from '@shared/common/routing/IRouteStructure';

export type AdminAccessRole =
  | 'admin'
  | 'user-admin'
  | 'developer'
  | 'translator'
  | 'knowledge-center-admin'
  | 'symptom-admin'
  | 'applications-reviewer';

export interface AdminNavigationItem extends IRouteStructure {
  requiredRoles: AdminAccessRole[];
}

export const ADMIN_NAVIGATION_ITEMS: AdminNavigationItem[] = [
  {
    ...ClinicianDashboardRoutes.USER_ADMIN.list,
    translationDefault: 'User admin',
    section: 'general',
    requiredRoles: ['user-admin'],
  },
  {
    ...ClinicianDashboardRoutes.FORMS.view,
    section: 'general',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.INT_MONITORING.view,
    section: 'general',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/onboarding-cfg`,
    localeKey: 'Journey',
    translationDefault: 'Journey',
    icon: 'mdi-account-box-outline',
    section: 'general',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/consent-documents`,
    localeKey: 'ConsentDocuments',
    translationDefault: 'Legal Terms',
    icon: IconMap.documentEdit,
    section: 'general',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/prescriptions/admin`,
    localeKey: 'Prescriptions',
    translationDefault: 'Prescriptions admin',
    icon: 'mdi-pill',
    section: 'general',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/translation`,
    localeKey: 'Translation',
    translationDefault: 'Translations',
    icon: 'mdi-translate',
    section: 'general',
    requiredRoles: ['translator'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/articles`,
    localeKey: 'Library',
    translationDefault: 'Library',
    icon: IconMap.bookOpenPageVariantOutline,
    section: 'general',
    requiredRoles: ['knowledge-center-admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/symptoms`,
    localeKey: 'Symptoms',
    translationDefault: 'Symptoms',
    icon: 'mdi-stethoscope',
    section: 'general',
    requiredRoles: ['symptom-admin', 'admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/applications`,
    localeKey: 'Applications',
    translationDefault: 'Applications',
    icon: 'mdi-stethoscope',
    section: 'general',
    requiredRoles: ['applications-reviewer'],
  },
  {
    ...ClinicianDashboardRoutes.ORGANISATIONS.view,
    translationDefault: 'Organisations',
    section: 'organisation',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.WORKSPACE_SETTINGS.view,
    translationDefault: 'Workspace settings',
    section: 'organisation',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.UI_COMPONENTS.view,
    translationDefault: 'UI Components',
    section: 'general',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.DEVELOPER.migrationCommandCenter,
    translationDefault: 'Migration command center',
    section: 'developer',
    requiredRoles: ['developer'],
  },
];

export function getAccessibleAdminNavigationItems(
  hasRole: (role: AdminAccessRole) => boolean,
): AdminNavigationItem[] {
  return ADMIN_NAVIGATION_ITEMS.filter(item => item.requiredRoles.some(role => hasRole(role)));
}

export function getFirstAccessibleAdminNavigationItem(
  hasRole: (role: AdminAccessRole) => boolean,
): AdminNavigationItem | null {
  return getAccessibleAdminNavigationItems(hasRole)[0] ?? null;
}

export function isAdminNavigationUrl(url: string): boolean {
  const normalizedUrl = normalizeUrl(url);

  return ADMIN_NAVIGATION_ITEMS.some(item => {
    const itemUrl = normalizeUrl(item.fullUrl as string);
    return normalizedUrl === itemUrl || normalizedUrl.startsWith(`${itemUrl}/`);
  });
}

function normalizeUrl(url: string): string {
  return (url ?? '').split('?')[0].split('#')[0];
}
